<template>
  <div class="">
    <div class="flex flex-col space-y-2">
      <div>
        <reservation-service
          v-for="(service, index) in _services"
          :key="`service-${index}`"
          :service="service"
          :allow-remove="allowRemove"
          :allow-edit="allowEdit"
          :service-type="serviceType"
          :default-employee="defaultEmployee"
          @remove="handleRemoveService(index)"
          @edit="handleEditService(service, index)"
        />
      </div>
    </div>
    <div class="mt-4 flex items-center justify-between">
      <div>
        <o-button v-if="allowAdd" flat size="sm" @click="handleAddService">
          {{ $t('service.addService') }}
        </o-button>
      </div>
    </div>

    <edit-service-modal
      ref="editServiceModal"
      :serviceType="serviceType"
      :reservation="reservation"
    />
  </div>
</template>

<script setup lang="ts">
import ReservationService from './Service.vue'
import EditServiceModal from '@/components/reservation/actions/EditService.vue'

import { ServiceCustomerType } from '@/_api/models/service'
import { ReservationServiceModel } from '@/_api/models/reservationServiceModel'

interface Props {
  services: any[]
  reservation: Record<string, any>
  defaultService?: object | null
  defaultEmployee?: Record<string, any> | null
}

interface Emits {
  (e: 'update:services', value: any[]): void
}

const props = withDefaults(defineProps<Props>(), {
  defaultService: null,
  defaultEmployee: null,
})

const emit = defineEmits<Emits>()

const editServiceModal = ref<any>()

const _services = computed({
  get () {
    return props.services
  },
  set (services) {
    emit('update:services', services)
  },
})

const serviceDefaults = computed(() => {
  const service = props.defaultService as any
  const employee = service && service.employeeIDs.includes(props.defaultEmployee!.idEmployee) ? props.defaultEmployee : 0

  const previousService = _services.value[_services.value.length - 1]
  const startTime = previousService ? previousService.startTime + previousService.duration : 0

  return {
    service,
    employee,
    startTime,
    duration: 0,
    finishing: 0,
    processing: 0,
    bufferTimePre: 0,
    bufferTimePost: 0,
    price: null,
  }
})

const allowAdd = computed(() => props.reservation.allow.edit && hasCompleteServices.value && !hasGroupService.value)
const allowRemove = computed(() => props.reservation.allow.edit && _services.value.length > 1)
const allowEdit = computed(() => props.reservation.allow.edit)

const serviceType = computed(() => {
  if (onlyGroupService.value || (hasMultipleServices.value && hasGroupService.value)) {
    return 'group'
  }

  if (onlySingleService.value || (hasMultipleServices.value && hasSingleService.value)) {
    return 'single'
  }

  return null
})

const hasService = computed(() => !!_services.value.length)
const hasMultipleServices = computed(() => _services.value.length > 1)
const hasCompleteServices = computed(() => _services.value.every((service: any) => !!service.service))
const hasSingleService = computed(() => hasService.value && _services.value.some((service: any) => service.service && service.service.customerType === ServiceCustomerType.single))
const hasGroupService = computed(() => hasService.value && _services.value.some((service: any) => service.service && service.service.customerType === ServiceCustomerType.group))
const onlyGroupService = computed(() => props.reservation.isGroup)
const onlySingleService = computed(() => props.reservation.isSingle)
const isReservationClass = computed(() => props.reservation.isGroup)

function handleAddService () {
  const service = new ReservationServiceModel({ ...serviceDefaults.value })
  service.employee = 0

  editServiceModal.value.open(service, (created: any) => {
    _services.value.push(created)
  })
}

function handleRemoveService (index: number) {
  _services.value.splice(index, 1)
}

function handleEditService (service: any, index: number) {
  editServiceModal.value.open(service, (updated: any) => {
    _services.value[index] = updated
  })
}

function handleClearServices () {
  const service = new ReservationServiceModel({ ...serviceDefaults.value })
  service.employee = 0
  _services.value = [service]
}

// created logic
if (!_services.value.length) {
  handleAddService()
}
</script>
